<template>
  <b-container class="usermanagement">
    <b-row>
      <b-col cols="12" md="10" offset-md="1" lg="8" offset-lg="2">
        <h2>Internal User Management</h2>
        <b-button variant="primary" class="mx-1 my-1" @click="showAddUser">Add New User</b-button>
        <b-button variant="primary" class="mx-1 my-1" @click="refreshUsers">Refresh User List</b-button>
        <b-list-group class="my-2">
          <b-list-group-item v-for="user in this.userList" :key="user.email" class="d-flex align-items-center">
            <b-avatar class="mr-3" variant="success"></b-avatar>
            <span class="mr-auto"
              >{{ user.email }}  ({{ user.role }})</span
            >
            <b-button class="mx-2" @click="showModifyUser(user.email, user.role)" variant="outline-primary">
              <b-icon icon="gear"></b-icon>
            </b-button>
            <b-button
              class="mx-2"
              @click="showRemoveUser(user.email)"
              variant="outline-danger"
              :disabled="currentUser.username === user.email"
            >
              <b-icon icon="person-x"></b-icon>
            </b-button>
          </b-list-group-item>
        </b-list-group>
      </b-col>
    </b-row>
    <b-modal
      ref="newUserModal"
      title="Create new user"
      hide-header-close
      @ok="addUser"
      :ok-disabled="!newUserFormValid"
    >
      <b-form-group
        label="User email:"
        label-for="newUserEmail"
        :state="newUserEmailState"
        :invalid-feedback="newUserEmailInvalidFeedback"
      >
        <b-form-input id="newUserEmail" v-model="newUserEmail"></b-form-input>
      </b-form-group>
      <b-form-group label="Role:" label-for="newUserRole" :state="newUserRoleState" :invalid-feedback="requiredText">
        <b-form-select id="newUserRole" v-model="newRole" :options="roles"></b-form-select>
      </b-form-group>
      <b-form-group
        label="Password:"
        label-for="newUserPassword"
        :state="userPasswordState"
        :invalid-feedback="newUserPasswordFeedback"
      >
        <b-form-input id="newUserPassword" v-model="password" type="password"></b-form-input>
      </b-form-group>
      <b-form-group
        label="Password (repeat):"
        label-for="newUserPasswordConfirm"
        :state="userPasswordConfirmState"
        :invalid-feedback="newUserPasswordConfirmFeedback"
      >
        <b-form-input id="newUserPasswordConfirm" v-model="passwordConfirm" type="password"></b-form-input>
      </b-form-group>
    </b-modal>
    <b-modal
      ref="modifyUserModal"
      title="Change user settings"
      hide-header-close
      @ok="modifyUser"
      :ok-disabled="!modifyUserFormValid"
    >
      <p>Leave a field empty to leave it unchanged.</p>
      <label>User Email:</label>
      <b-form-input v-model="modifyUserEmail" disabled></b-form-input>
      <b-form-group
        label="New password:"
        label-for="modifyUserPassword"
        :state="userPasswordState"
        :invalid-feedback="modifyUserPasswordFeedback"
      >
        <b-form-input id="modifyUserPassword" v-model="password" type="password"></b-form-input>
      </b-form-group>
      <b-form-group
        label="New password (repeat):"
        label-for="modifyUserPasswordConfirm"
        :state="userPasswordConfirmState"
        :invalid-feedback="modifyUserPasswordConfirmFeedback"
      >
        <b-form-input id="modifyUserPasswordConfirm" v-model="passwordConfirm" type="password"></b-form-input>
      </b-form-group>
      <b-form-group label="Role:" label-for="selectedRole">
        <b-form-select id="selectedRole" v-model="selectedRole" :options="roles"></b-form-select> 
      </b-form-group>
    </b-modal>
    <b-modal ref="removeUserModal" title="Delete user?" hide-header-close @ok="removeUser">
      <template #modal-ok>Confirm</template> 
      <label>User Email:</label>
      <b-form-input v-model="deleteUserEmail" disabled></b-form-input>
    </b-modal>
  </b-container>
</template>
<script>
import { ROLES } from '@/const'
import { defaultToastBody, defaultToastConfig } from '@/util'

// Regex taken from https://stackoverflow.com/questions/46155/whats-the-best-way-to-validate-an-email-address-in-javascript
// Not the accepted answer (overly verbose, we just want basic validation on client side)
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export default {
  name: 'UserManagement',
  data: function () {
    return {
      newUserEmail: '',
      newRole: null,
      selectedGroup: null,
      selectedRole: null,
      modifyUserEmail: '',
      deleteUserEmail: '',
      password: '',
      passwordConfirm: '',
      groupOptions: [],
      roles: Object.values(ROLES),
      requiredText: 'Required',
    }
  },
  computed: {
    currentUser: function () {
      return this.$store.state.user
    },
    userList: function () {
      return this.$store.state.userList
    },
    newUserEmailState() {
      return this._validateUser(this.newUserEmail)
    },
    newUserEmailInvalidFeedback() {
      return this.newUserEmail.length === 0 ? 'Required' : 'Must be a valid email'
    },
    newUserRoleState() {
      return this._validateRole(this.newRole)
    },
    userPasswordState() {
      return this._validatePassword(this.password)
    },
    newUserPasswordFeedback() {
      return this.password.length === 0 ? 'Required' : 'Must be at least 8 characters'
    },
    userPasswordConfirmState() {
      return this.passwordConfirm === this.password
    },
    newUserPasswordConfirmFeedback() {
      if (this.passwordConfirm.length === 0) {
        return 'Required'
      }
      if (this.passwordConfirm != this.password) {
        return 'Passwords do not match'
      }
      return ''
    },
    newUserFormValid() {
      return (
        this._validateUser(this.newUserEmail) &&
        this._validateRole(this.newRole) &&
        this._validatePassword(this.password) &&
        this.password == this.passwordConfirm
      )
    },
    modifyUserPasswordFeedback() {
      if (this.password && this.password.length <= 8) {
        return 'Must be at least 8 characters'
      }
      return ''
    },
    modifyUserPasswordConfirmFeedback() {
      if (this.passwordConfirm != this.password) {
        return 'Passwords do not match'
      }
      return ''
    },
    modifyUserFormValid() {
      if (!this.password && !this.passwordConfirm) {
        return true
      }
      return this._validatePassword(this.password) && this.password == this.passwordConfirm
    },
  },
  methods: {
    _validateUser: function (userEmail) {
      // Checks if userEmail is a valid email
      return EMAIL_RE.test(userEmail)
    },
    _validateRole(role) {
      return role != null
    },
    _validatePassword: function (password) {
      return password.length >= 8
    },
    showAddUser: function () {
      this.newUserEmail = ''
      this.password = ''
      this.passwordConfirm = ''
      this.newRole = null
      this.selectedGroup = null
      this.$refs['newUserModal'].show()
    },
    addUser: function () {
      // TODO: This should be in a validation routine, not on submit (closes modal)
      if (this.password != this.passwordConfirm) {
        let toastConfig = defaultToastConfig()
        toastConfig.title = 'Passwords mismatch'
        this.$bvToast.toast(`Passwords don't match, please try again.`, toastConfig)
        return
      }
      let payload = {
        email: this.newUserEmail,
        password: this.password,
        role: this.newRole,
      }
      this.$store
        .dispatch('createUser', payload)
        .then(() => {
          let toastConfig = defaultToastConfig()
          toastConfig.title = 'New user created!'
          toastConfig.variant = 'success'
          this.$bvToast.toast(`Created user ${this.newUserEmail}`, toastConfig)
          this.newUserEmail = ''
          this.password = ''
          this.passwordConfirm = ''
          this.newRole = null
          this.selectedGroup = null
          this.refreshUsers()
        })
        .catch((err) => {
          this.$bvToast.toast(defaultToastBody(err), defaultToastConfig(err))
        })
    },
    showModifyUser: function (userEmail, userRole) {
      this.modifyUserEmail = userEmail
      this.password = ''
      this.passwordConfirm = ''
      this.selectedRole = userRole
      this.$refs['modifyUserModal'].show()
    },
    modifyUser: function () {
      // TODO: Verify passwords match (also in navigation)
      let payload = {
        email: this.modifyUserEmail,
        role: this.selectedRole,
      }
      if (this.password.length > 0) {
        payload.new_password = this.password
      }
      this.$store
        .dispatch('modifyUser', payload)
        .then(() => {
          let toastConfig = defaultToastConfig()
          toastConfig.title = 'User updated!'
          toastConfig.variant = 'primary'
          this.$bvToast.toast(`Modified user ${this.newUserEmail}`, toastConfig)
          this.newUserEmail = ''
          this.password = ''
          this.passwordConfirm = ''
          this.selectedGroup = null
          this.refreshUsers()
        })
        .catch((err) => {
          console.error(err)
          this.$bvToast.toast(defaultToastBody(err), defaultToastConfig(err))
        })
    },
    showRemoveUser: function (email) {
      this.deleteUserEmail = email
      this.$refs['removeUserModal'].show()
    },
    removeUser: function () {
      this.$store
        .dispatch('deleteUser', this.deleteUserEmail)
        .then(() => {
          let toastConfig = defaultToastConfig()
          toastConfig.title = `User "${this.deleteUserEmail}" deleted!`
          toastConfig.variant = 'warning'
          this.$bvToast.toast(`Deleted user ${this.newUserEmail}`, toastConfig)
          this.deleteUserEmail = ''
          this.refreshUsers()
        })
        .catch((err) => {
          this.$bvToast.toast(defaultToastBody(err), defaultToastConfig(err))
        })
    },
    refreshUsers: function () {
      // TODO: Set loading state to true
      this.$store
        .dispatch('getUsers')
        .then(() => {
          // TODO: Set loading state to false
        })
        .catch((err) => {
          this.$bvToast.toast(defaultToastBody(err), defaultToastConfig(err))
        })
    },
  },
  components: {},
  created() {},
  mounted() {
    // If we have no users loaded, start the request immediately.
    if (this.$store.state.userList.length === 0) {
      this.refreshUsers()
    }
  },
}
</script>
